import type { ListBuilderConfiguration } from '../types';

/**
 * License Disciplinary Actions embedded list configuration.
 *
 * Drives the Disciplinary tab on the license-detail page. The data source
 * is `GET /api/licenses/:id/disciplinary-actions`, which when called with
 * any of `page/limit/search/filters/sortFields` URL params routes to
 * `LicenseDisciplinaryService.findAll`. Filters in this list come from
 * `libs/core/src/filters/config/license-disciplinary.ts`
 * (`discStatus` / `discSeverity` / `discDateRange`).
 *
 * Per-row Edit / Delete is wired through the `actions_dropdown` column at
 * the end. The tab component owns the sidebar / confirm dialog state and
 * passes `editAction` / `deleteAction` callbacks to the list via
 * `callbacks`. Add is handled by a header action in the tab's wrapper
 * (not by this config) so the existing `DisciplinaryActionSidebar`
 * keeps owning the create/edit lifecycle.
 */
export const LICENSE_DISCIPLINARY_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-license-disciplinary-embedded',
			cacheTTL: 300000,
			name: 'Disciplinary Actions',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'action-type-column',
					fields: [
						{
							id: 'action-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'actionType',
									objectTable: 'license_disciplinary_action',
								},
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'action-type-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'action-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'actionDate',
									objectTable: 'license_disciplinary_action',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { dateFormat: 'date-medium' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-action-type', text: 'Action' }],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'actionDate',
							objectTable: 'license_disciplinary_action',
						},
					],
					minWidth: 220,
				},
				{
					id: 'severity-column',
					fields: [
						{
							id: 'severity-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'severity',
									objectTable: 'license_disciplinary_action',
								},
								defaultValue: 'Medium',
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'status_map',
									statusMap: {
										Critical: { type: 'danger' },
										High: { type: 'danger' },
										Medium: { type: 'warning' },
										Low: { type: 'info' },
									},
								},
							},
						},
					],
					headers: [{ id: 'h-severity', text: 'Severity' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'severity',
							objectTable: 'license_disciplinary_action',
						},
					],
					width: 110,
				},
				{
					id: 'status-column',
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'status',
									objectTable: 'license_disciplinary_action',
								},
								defaultValue: 'Open',
							},
							displayStyle: 'badge',
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'status_map',
									statusMap: {
										Open: { type: 'warning' },
										Resolved: { type: 'success' },
										Dismissed: { type: 'default' },
									},
								},
							},
						},
					],
					headers: [{ id: 'h-status', text: 'Status' }],
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'status',
							objectTable: 'license_disciplinary_action',
						},
					],
					width: 110,
				},
				{
					id: 'authority-case-column',
					fields: [
						{
							id: 'issuing-authority-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'issuingAuthority',
									objectTable: 'license_disciplinary_action',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'authority-case-break',
							dataConfig: { type: 'custom_text', customText: '' },
							displayStyle: 'line_break',
						},
						{
							id: 'case-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'caseNumber',
									objectTable: 'license_disciplinary_action',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-authority', text: 'Authority / Case #' }],
					sortable: false,
					minWidth: 180,
				},
				{
					id: 'fine-column',
					fields: [
						{
							id: 'fine-amount-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'fineAmount',
									objectTable: 'license_disciplinary_action',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								formatOptions: { numberFormat: 'currency' },
							},
							displayStyle: 'number',
						},
					],
					headers: [{ id: 'h-fine', text: 'Fine' }],
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'fineAmount',
							objectTable: 'license_disciplinary_action',
						},
					],
					width: 110,
				},
				{
					id: 'description-column',
					fields: [
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'license_disciplinary_action',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h-description', text: 'Description' }],
					sortable: false,
					minWidth: 220,
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'edit',
									label: 'Edit',
									callbackName: 'editAction',
								},
								{
									id: 'delete',
									label: 'Delete',
									callbackName: 'deleteAction',
									variant: 'danger',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					sortable: false,
					width: 60,
				},
			],
			defaultSortId: 'date-desc',
		},
	],
	// Shared table-level sort options live at the configuration root and use the
	// TableSortOption shape (`name` + `fields`) — the resolver reads
	// `configuration.tableSorts`, so a list-nested block or `label`/`sortFields`
	// keys silently fail to resolve `defaultSortId` (PD-14531 / PD-14533).
	tableSorts: [
		{
			id: 'date-desc',
			name: 'Newest first',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'actionDate',
					objectTable: 'license_disciplinary_action',
				},
			],
		},
		{
			id: 'date-asc',
			name: 'Oldest first',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'actionDate',
					objectTable: 'license_disciplinary_action',
				},
			],
		},
		{
			id: 'severity-desc',
			name: 'Most severe first',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'severity',
					objectTable: 'license_disciplinary_action',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	showResetButton: true,
	filters: {
		activeFilters: ['discStatus', 'discSeverity', 'discDateRange'],
	},
	objects: [
		{
			baseUrl: '/licenses/:id/disciplinary-actions',
			objectLabel: 'Disciplinary Action',
			objectTable: 'license_disciplinary_action',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'actionType', fieldLabel: 'Action Type' },
				{ fieldName: 'actionDate', fieldLabel: 'Action Date' },
				{ fieldName: 'severity', fieldLabel: 'Severity' },
				{ fieldName: 'status', fieldLabel: 'Status' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'issuingAuthority', fieldLabel: 'Authority' },
				{ fieldName: 'caseNumber', fieldLabel: 'Case Number' },
				{ fieldName: 'fineAmount', fieldLabel: 'Fine Amount' },
				{ fieldName: 'finePaid', fieldLabel: 'Fine Paid' },
				{ fieldName: 'resolutionDate', fieldLabel: 'Resolution Date' },
				{ fieldName: 'resolution', fieldLabel: 'Resolution' },
			],
		},
	],
};
